import { apiClient } from "./api-client";

// Security Audit — read-only, tenant-scoped, newest-first. Mirrors the backend
// AdminSecurityAuditController (/admin/api/v1/security-audit), gated ADMIN_SUPER.

export type SecurityAuditStatus = "SUCCESS" | "FAILURE";

export interface SecurityAuditView {
  id: number;
  actorUserId?: number;
  actorLevel?: string;
  targetUserId?: number;
  action?: string;
  status?: SecurityAuditStatus | string;
  failureReason?: string;
  ip?: string;
  userAgent?: string;
  requestId?: string;
  createdAt?: string;
}

export function listSecurityAudit(page = 0, size = 20): Promise<SecurityAuditView[]> {
  return apiClient.request<SecurityAuditView[]>(
    `/admin/api/v1/security-audit?page=${page}&size=${size}`,
  );
}

// Readable labels for the SecurityAuditAction enum.
const ACTION_LABELS: Record<string, string> = {
  LOGIN: "Sign in",
  LOGOUT: "Sign out",
  MFA_ENROLL: "MFA enrolled",
  MFA_VERIFY: "MFA verified",
  PASSWORD_SETUP: "Password set",
  PASSWORD_RESET: "Password reset",
  PASSWORD_CHANGE: "Password changed",
  OTP_ISSUED: "OTP issued",
  OTP_VERIFIED: "OTP verified",
  ADMIN_CREATED: "Admin created",
  ADMIN_UPDATED: "Admin updated",
  ADMIN_ACTIVATED: "Admin activated",
  ADMIN_DEACTIVATED: "Admin suspended",
  ADMIN_DELETED: "Admin deleted",
  ACCOUNT_LOCKED: "Account locked",
  ACCOUNT_UNLOCKED: "Account unlocked",
  ROLE_CHANGED: "Role changed",
};

export function auditActionLabel(action: string | undefined): string {
  if (!action) return "—";
  return ACTION_LABELS[action] ?? action.replace(/_/g, " ").toLowerCase();
}

/** Best-effort browser/OS from a raw user-agent (the backend stores only the raw UA). */
export function describeUserAgent(ua: string | undefined): string {
  if (!ua) return "—";
  const browser = /Edg\//.test(ua)
    ? "Edge"
    : /Chrome\//.test(ua)
      ? "Chrome"
      : /Firefox\//.test(ua)
        ? "Firefox"
        : /Safari\//.test(ua)
          ? "Safari"
          : "Browser";
  const os = /Windows/.test(ua)
    ? "Windows"
    : /Mac OS X|Macintosh/.test(ua)
      ? "macOS"
      : /Android/.test(ua)
        ? "Android"
        : /iPhone|iPad|iOS/.test(ua)
          ? "iOS"
          : /Linux/.test(ua)
            ? "Linux"
            : "";
  return os ? `${browser} · ${os}` : browser;
}
